import { useCallback, useMemo } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import type { PlayerView } from '@sh/shared'
import { ActionSheet } from '../components/ActionSheet.js'
import { Attribution } from '../components/Attribution.js'
import { POWER_LABEL } from '../components/Board.js'
import { ConfirmButton } from '../components/Confirm.js'
import { AppMark } from '../components/Icons.js'
import { BoardPanel } from '../components/BoardPanel.js'
import { Cinematic } from '../components/Cinematic.js'
import { Connecting } from '../components/Connecting.js'
import { HoldHintProvider } from '../components/HoldHint.js'
import { SecretDrawer } from '../components/SecretDrawer.js'
import { NudgeProvider } from '../components/Nudge.js'
import { usePlayerGame } from '../net/useGame.js'
import { useGameEvents } from '../net/useGameEvents.js'
import { useWakeLock } from '../net/useDevice.js'
import { SkinProvider } from '../skin/SkinContext.js'
import { LobbyPhase } from './phases/Lobby.js'
import { RevealPhase } from './phases/Reveal.js'
import { NominationPhase, VotingPhase } from './phases/Election.js'
import { ChancellorPhase, PresidentPhase, VetoPhase } from './phases/Legislative.js'
import { ExecutivePhase } from './phases/Executive.js'
import { GameOverPhase } from './phases/GameOver.js'
import { HostTools } from './phases/HostTools.js'

/** What the nudge says when the table starts waiting on you. */
function turnLabel(view: PlayerView): string {
  switch (view.phase) {
    case 'role_reveal':
      return 'check your role'
    case 'nomination':
      return 'nominate a Chancellor'
    case 'voting':
      return 'cast your ballot'
    case 'legislative_president':
      return 'discard a policy'
    case 'legislative_chancellor':
      return 'enact a policy'
    case 'veto_pending':
      return 'answer the veto'
    case 'executive_action':
      // Named, not "use your power". This screen is the first thing a phone
      // shows the moment it becomes your turn, and "use your power" sent people
      // to the board to find out which one they had been handed.
      return view.pendingPower ? `use ${POWER_LABEL[view.pendingPower]}` : 'use your power'
    default:
      return ''
  }
}

/**
 * The win does not time out on a phone. Every other scene interrupts something
 * you are waiting to get back to; this one is the end of the evening, and it
 * should still be there when you look up from the table.
 */
const PHONE_CINEMATICS = { holdForTap: ['victory'] } as const

/**
 * The phases that ask the player for something, and so get docked rather than
 * left in the page flow.
 *
 * This is every phase the engine's own `waitingOn` can name, which is the
 * point: if the table is waiting on this phone, the control it is waiting for
 * is on screen and stays on screen. The lobby and the result are not here -
 * nothing is outstanding on either, and the host's start button is at the top
 * of a short page it could not fall off.
 */
const SHEET_PHASES: readonly PlayerView['phase'][] = [
  'role_reveal',
  'nomination',
  'voting',
  'legislative_president',
  'legislative_chancellor',
  'veto_pending',
  'executive_action',
]

export function Play() {
  const { code = '' } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const { view, status, error, act, setError, leave } = usePlayerGame(code.toUpperCase())
  const { event: playing, skip, awaitingTap } = useGameEvents(view, view?.you?.id ?? null, PHONE_CINEMATICS)

  const yourTurn = useMemo(
    () => (view?.you ? view.waitingOn.includes(view.you.id) : false),
    [view],
  )
  const label = view ? turnLabel(view) : ''
  const signature = view ? `${view.phase}:${view.presidentId}:${view.liberalPolicies}:${view.fascistPolicies}` : ''

  /**
   * Going back to the main screen, in the one place it is defined: the mark in
   * the corner and the lobby's own button are two doors onto this, not two
   * behaviours. Leaving is giving the seat up rather than merely changing
   * route - a phone that only navigated away would leave a name in the seat
   * list that nobody is behind, and the host counting heads would count it -
   * so the stored token goes only once the server has actually taken the seat
   * out, and a refused leave leaves this device holding what it holds.
   */
  const goHome = useCallback(async () => {
    const ack = await act({ type: 'player_leave' })
    if (!ack.ok) return
    leave()
    navigate('/')
  }, [act, leave, navigate])

  useWakeLock(Boolean(view) && view?.phase !== 'game_over')

  if (status === 'rejected') {
    return (
      <main className="shell">
        <section className="card card--note">
          <h2 className="card__title">No seat on this device</h2>
          <p>{error ?? 'This browser is not holding a seat in that lobby.'}</p>
          <Link className="btn btn--primary btn--block" to={`/join/${code}`}>
            Join lobby {code}
          </Link>
        </section>
        <Attribution />
      </main>
    )
  }

  if (!view) {
    return (
      <main className="shell" data-testid="play-connecting">
        <Connecting code={code.toUpperCase()} status={status} />
      </main>
    )
  }

  const phase = view.phase
  // Exactly the engine's own rule for handing a seat back: before the deal and
  // after the result, never in between.
  const canLeave = phase === 'lobby' || phase === 'game_over'
  /*
    Your own role, re-readable, at the foot of the seat list on the board.
    Only while there is a game to keep it from: before the deal there is no
    role, and once it is over every role on the table is public anyway.
  */
  const secret =
    phase !== 'game_over' && view.you?.role ? <SecretDrawer view={view} /> : undefined
  /*
    The table is waiting on this phone for something it can actually do here,
    so the control is docked to the bottom of the viewport instead of sitting
    in a column above a board several screens tall. The moment the thing is
    done - a ballot cast, a role acknowledged - `waitingOn` drops this seat and
    the card goes back into the flow with everything else.
  */
  const docked = yourTurn && SHEET_PHASES.includes(phase)

  /*
    The one phase switch, rendered either into the dock or into the column.
    Declared once rather than twice: two copies of this list is two places for
    a new phase to be forgotten.
  */
  const controls = (
    <>
      {phase === 'lobby' && (
        <LobbyPhase view={view} code={code.toUpperCase()} act={act} leave={goHome} />
      )}
      {phase === 'role_reveal' && <RevealPhase view={view} act={act} />}
      {phase === 'nomination' && <NominationPhase view={view} act={act} />}
      {phase === 'voting' && <VotingPhase view={view} act={act} />}
      {phase === 'legislative_president' && <PresidentPhase view={view} act={act} />}
      {phase === 'legislative_chancellor' && <ChancellorPhase view={view} act={act} />}
      {phase === 'veto_pending' && <VetoPhase view={view} act={act} />}
      {phase === 'executive_action' && <ExecutivePhase view={view} act={act} />}
      {phase === 'game_over' && <GameOverPhase view={view} code={code.toUpperCase()} act={act} />}
    </>
  )

  return (
    <SkinProvider skinId={view.config.skin}>
    <HoldHintProvider>
    {/*
      Held back while a cinematic is running, so the nudge is not spent behind
      a policy reveal, and never on the result screen - by then the game is
      over and the phone is asking for nothing.
    */}
    <NudgeProvider
      active={yourTurn && !playing && phase !== 'game_over'}
      anchored={docked}
      signature={signature}
      label={label}
    >
    <main
      className={`shell ${docked ? 'shell--sheet' : ''}`}
      data-phase={phase}
      data-testid="play"
    >
      <Cinematic event={playing} onSkip={skip} awaitingTap={awaitingTap} />

      <header className="shell__head">
        {/*
          The mark and the code, rather than the mark and your own name. You
          know who you are; the code is the one thing about this screen a
          player gets asked for out loud - a phone that has just died is read
          the code off the nearest live one - and hunting it down meant leaving
          the game for the lobby or the board. It is a label beside the mark,
          not a control: the two places it is acted on, joining and reclaiming
          a seat, still print it where the typing happens.

          In that corner a mark is taken for the way out, so here it is one,
          two-tap like every other thing the engine will not undo. It leads out
          only where the engine lets a seat go - before the deal and after the
          result. Mid-game it is just the mark: a button that could only refuse
          is worse than no button, and a table waiting on you is not a thing to
          offer an exit from.
        */}
        {canLeave ? (
          <ConfirmButton
            className="shell__mark"
            confirmLabel="leave?"
            ariaLabel="Leave and go to the main screen"
            onConfirm={goHome}
            testId="play-home"
          >
            <AppMark />
          </ConfirmButton>
        ) : (
          <span className="shell__mark" role="img" aria-label="Secret Hitler">
            <AppMark />
          </span>
        )}
        {/*
          The code and the practice stamp on one paper label, the way a case
          file is tagged. Both are stamp red, which is a colour for paper: on
          the desk it measured 2.6:1 and the corner simply was not there.
        */}
        <span className="shell__tag">
          <span className="shell__code" aria-label={`game code ${code.toUpperCase()}`} data-testid="play-code">
            {code.toUpperCase()}
          </span>
          {view.config.practice === true && (
            <span className="shell__practice" data-testid="play-practice">
              practice
            </span>
          )}
        </span>
        <span className={`shell__status shell__status--${status}`}>
          {status === 'live' ? 'connected' : status === 'offline' ? 'reconnecting…' : '…'}
        </span>
        {/*
          An in-app link rather than a new tab: the socket is a singleton for
          the tab and is not torn down by a route change, so a player reading
          the rules mid-round does not blink offline at the table.
        */}
        <Link className="shell__rules" to="/rules" data-testid="play-rules" aria-label="How to play">
          rules
        </Link>
        {/*
          Beside the rules link, because it belongs to the same register: things
          the app can do for you, rather than things the game is asking of you.
          It opens over the page; nothing of it is on screen the rest of the time.
        */}
        <HostTools view={view} code={code.toUpperCase()} act={act} />
      </header>

      {error && (
        <p className="error" role="alert" data-testid="action-error" onClick={() => setError(null)}>
          {error}
        </p>
      )}

      {/*
        The controls, wrapped rather than left bare, because a laptop sets this
        column beside the board and the grid needs one thing to put in it.
      */}
      <div className="shell__controls">
        {docked ? (
          <ActionSheet label={label} signature={signature} offline={status === 'offline'}>
            {controls}
          </ActionSheet>
        ) : (
          <div className="shell__body">{controls}</div>
        )}
      </div>

      {/*
        Under the controls, not over them. What you have to do comes first and
        the board you are deciding it against sits beneath it, which is also the
        order that lets a laptop set the two of them side by side - see the
        two-column rule in the stylesheet.

        The result screen docks the same board rather than laying out one of its
        own. It is the same game and the same board a second earlier; giving it
        a different shape made the end of a game look like a different app.
      */}
      {phase !== 'lobby' && <BoardPanel view={view} secret={secret} />}

      {/*
        The licence, which is a condition rather than decoration and was simply
        missing from the one screen a player spends a whole evening on. It sits
        at the foot of the page, under both columns.
      */}
      <Attribution />
    </main>
    </NudgeProvider>
    </HoldHintProvider>
    </SkinProvider>
  )
}
